import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Typography, Box, Divider} from 'views/components';
import {Translate, translate} from 'views/module';

const SkillsAndExp = (props) => {
  const {bioQuestions, setIsComplete} = props;

  const resolvedSkillsAndExp = React.useMemo(() => {
    const careTasks = bioQuestions?.careTasks || null;
    const memoryCare = bioQuestions?.memoryCare || null;
    const mobilitySupport = bioQuestions?.mobilitySupport || null;
    const personalCare = bioQuestions?.personalCare || null;
    const medicationReminders = bioQuestions?.medicationReminders || null;
    const mealPreparation = bioQuestions?.mealPreparation || null;
    const lightHousekeeping = bioQuestions?.lightHousekeeping || null;
    const skillsExperienceCompleted = Boolean(bioQuestions?.skillsExperienceCompleted);
    const hasAnyData = Boolean(careTasks || memoryCare || mobilitySupport || personalCare || medicationReminders ||
                              mealPreparation || lightHousekeeping);

    if (!hasAnyData) {
      return {
        careTasks: null,
        memoryCare: null,
        mobilitySupport: null,
        personalCare: null,
        medicationReminders: null,
        mealPreparation: null,
        lightHousekeeping: null,
        skillsExperienceCompleted: false,
        hasAnyData: false,
      };
    }

    return {
      careTasks: careTasks?.selected || [],
      memoryCare,
      mobilitySupport,
      personalCare,
      medicationReminders,
      mealPreparation,
      lightHousekeeping,
      skillsExperienceCompleted,
      hasAnyData,
    };
  }, [bioQuestions]);

  React.useEffect(() => {
    setIsComplete(Boolean(resolvedSkillsAndExp.skillsExperienceCompleted));
  }, [resolvedSkillsAndExp]);

  if (!resolvedSkillsAndExp.hasAnyData) {
    return (
      <Stack spacing={1.5} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          <Translate expression="caregiver.CaregiverBiography.SkillsAndExp.EmptyState" />
        </Typography>
      </Stack>
    );
  }

  const {
    careTasks,
    memoryCare,
    mobilitySupport,
    personalCare,
    medicationReminders,
    mealPreparation,
    lightHousekeeping,
  } = resolvedSkillsAndExp;

  const getLevelLabel = (path, level) => {
    const levelValue = level?.value;

    if (!levelValue) {
      return translate('caregiver.CaregiverBiography.SkillsAndExp.Values.NotProvided');
    }

    return translate(`${path}.${levelValue[0].toUpperCase() + levelValue.slice(1)}`);
  };

  return (
    <Stack spacing={1.5} alignItems="stretch" sx={{width: '100%'}}>
      <Typography variant="subtitle1" sx={{fontWeight: 600}}>
        <Translate expression="caregiver.CaregiverBiography.SkillsAndExp.Summary.Title" />
      </Typography>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #0288d1',
          borderRadius: '12px',
          backgroundColor: '#e1f5fe',
          paddingY: 1,
          paddingX: 1.5,
          gap: 0.5,
        }}
      >
        <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 600}}>
          <Translate expression="caregiver.CaregiverBiography.SkillsAndExp.Title" />
        </Typography>
        {
          careTasks.includes('memoryCare') && memoryCare &&
            <Box>
              <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 500}}>
                {
                  `${translate('caregiver.CaregiverBiography.SkillsAndExp.Labels.MemoryCare.Label')} - ${
                    getLevelLabel('caregiver.CaregiverBiography.SkillsAndExp.Labels.MemoryCare.LevelOptions', memoryCare.level)
                  }:`
                }
              </Typography>
              <Box sx={{ml: 1}}>
                {
                  memoryCare.tasks.value.map((x, i) => {
                    return <Typography key={i} variant="body2" sx={{color: '#0288d1'}}>{
                      `• ${translate(`caregiver.CaregiverBiography.SkillsAndExp.Labels.MemoryCare.TaskOptions.${
                        x[0].toUpperCase()+x.slice(1)
                      }`)}`
                    }</Typography>;
                  })
                }
              </Box>
            </Box>
        }
        {
          careTasks.includes('mobilitySupport') && mobilitySupport &&
            <Box>
              <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 500}}>
                {
                  `${translate('caregiver.CaregiverBiography.SkillsAndExp.Labels.MobilitySupport.Label')} - ${
                    getLevelLabel('caregiver.CaregiverBiography.SkillsAndExp.Labels.MobilitySupport.LevelOptions', mobilitySupport.level)
                  }:`
                }
              </Typography>
              <Box sx={{ml: 1}}>
                {
                  mobilitySupport.tasks.value.map((x, i) => {
                    return <Typography key={i} variant="body2" sx={{color: '#0288d1'}}>{
                      `• ${translate(`caregiver.CaregiverBiography.SkillsAndExp.Labels.MobilitySupport.TaskOptions.${
                        x[0].toUpperCase()+x.slice(1)
                      }`)}`
                    }</Typography>;
                  })
                }
              </Box>
            </Box>
        }
        {
          careTasks.includes('personalCare') && personalCare &&
            <Box>
              <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 500}}>
                {
                  `${translate('caregiver.CaregiverBiography.SkillsAndExp.Labels.PersonalCare.Label')} - ${
                    getLevelLabel('caregiver.CaregiverBiography.SkillsAndExp.Labels.PersonalCare.LevelOptions', personalCare.level)
                  }:`
                }
              </Typography>
              <Box sx={{ml: 1}}>
                {
                  personalCare.tasks.value.map((x, i) => {
                    return <Typography key={i} variant="body2" sx={{color: '#0288d1'}}>{
                      `• ${translate(`caregiver.CaregiverBiography.SkillsAndExp.Labels.PersonalCare.TaskOptions.${
                        x[0].toUpperCase()+x.slice(1)
                      }`)}`
                    }</Typography>;
                  })
                }
              </Box>
            </Box>
        }
        {
          careTasks.includes('medicationReminders') && medicationReminders &&
            <Box>
              <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 500}}>
                {
                  `${translate('caregiver.CaregiverBiography.SkillsAndExp.Labels.MedicationReminders.Label')} - ${
                    getLevelLabel('caregiver.CaregiverBiography.SkillsAndExp.Labels.MedicationReminders.LevelOptions', medicationReminders.level)
                  }:`
                }
              </Typography>
              <Box sx={{ml: 1}}>
                {
                  medicationReminders.tasks.value.map((x, i) => {
                    return <Typography key={i} variant="body2" sx={{color: '#0288d1'}}>{
                      `• ${translate(`caregiver.CaregiverBiography.SkillsAndExp.Labels.MedicationReminders.TaskOptions.${
                        x[0].toUpperCase()+x.slice(1)
                      }`)}`
                    }</Typography>;
                  })
                }
              </Box>
            </Box>
        }
        {
          careTasks.includes('mealPreparation') && mealPreparation &&
            <Box>
              <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 500}}>
                {
                  `${translate('caregiver.CaregiverBiography.SkillsAndExp.Labels.MealPreparation.Label')} - ${
                    getLevelLabel('caregiver.CaregiverBiography.SkillsAndExp.Labels.MealPreparation.LevelOptions', mealPreparation.level)
                  }:`
                }
              </Typography>
              <Box sx={{ml: 1}}>
                {
                  mealPreparation.tasks.value.map((x, i) => {
                    return <Typography key={i} variant="body2" sx={{color: '#0288d1'}}>{
                      `• ${translate(`caregiver.CaregiverBiography.SkillsAndExp.Labels.MealPreparation.TaskOptions.${
                        x[0].toUpperCase()+x.slice(1)
                      }`)}`
                    }</Typography>;
                  })
                }
              </Box>
            </Box>
        }
        {
          careTasks.includes('lightHousekeeping') && lightHousekeeping &&
            <Box>
              <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 500}}>
                {
                  `${translate('caregiver.CaregiverBiography.SkillsAndExp.Labels.LightHousekeeping.Label')} - ${
                    getLevelLabel('caregiver.CaregiverBiography.SkillsAndExp.Labels.LightHousekeeping.LevelOptions', lightHousekeeping.level)
                  }:`
                }
              </Typography>
              <Box sx={{ml: 1}}>
                {
                  lightHousekeeping.tasks.value.map((x, i) => {
                    return <Typography key={i} variant="body2" sx={{color: '#0288d1'}}>{
                      `• ${translate(`caregiver.CaregiverBiography.SkillsAndExp.Labels.LightHousekeeping.TaskOptions.${
                        x[0].toUpperCase()+x.slice(1)
                      }`)}`
                    }</Typography>;
                  })
                }
              </Box>
            </Box>
        }
      </Box>
    </Stack>
  );
};

SkillsAndExp.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
  setIsComplete: PropTypes.func.isRequired,
};

export default React.memo(SkillsAndExp);
